// Entity layers and sources configuration
export const createEntitySources = (entities, entityAlertStates = {}) => {
  const sources = {};
  const entityLayersByType = {};

  // Group entities by entityTypeId
  const entityGroups = {};
  entities.forEach(entity => {
    const entityTypeId = entity.entityTypeId._id;
    if (!entityGroups[entityTypeId]) {
      entityGroups[entityTypeId] = {
        entityTypeId: entityTypeId,
        entityTypeName: entity.entityTypeId.name,
        entityTypeIcon: entity.entityTypeId.icon,
        entityTypeColor: entity.entityTypeId.iconColor || '#4169E1',
        entities: []
      };
    }
    entityGroups[entityTypeId].entities.push(entity);
  });

  // Create sources for each entity type
  Object.values(entityGroups).forEach((entityGroup) => {
    if (entityGroup.entities.length > 0) {
      const entityTypeId = entityGroup.entityTypeId;
      const sourceId = `entities-${entityTypeId}`;

      // Convert entities to GeoJSON format
      const geoJsonFeatures = entityGroup.entities
        .filter(entity => entity.isGeoLocated && entity.keys)
        .map(entity => {
          const latitudeKey = entity.keys.find(key => key.keyName === 'latitude');
          const longitudeKey = entity.keys.find(key => key.keyName === 'longitude');

          if (latitudeKey && longitudeKey) {
            const lat = parseFloat(latitudeKey.value);
            const lng = parseFloat(longitudeKey.value);

            if (!isNaN(lat) && !isNaN(lng)) {
              return {
                type: 'Feature',
                geometry: {
                  type: 'Point',
                  coordinates: [lng, lat]
                },
                properties: {
                  id: entity._id,
                  name: entity.name,
                  entityTypeId: entity.entityTypeId._id,
                  entityTypeName: entityGroup.entityTypeName,
                  color: entityGroup.entityTypeColor,
                  imageUrl: entity.image?.url || null,
                  icon: entity.entityTypeId.icon
                }
              };
            }
          }
          return null;
        })
        .filter(feature => feature !== null);

      if (geoJsonFeatures.length > 0) {
        // Add main entity source
        sources[sourceId] = {
          type: "geojson",
          data: {
            type: "FeatureCollection",
            features: geoJsonFeatures
          }
        };

        // Create ripple effect features only for entities with alerts
        const rippleFeatures = geoJsonFeatures
          .filter(feature => entityAlertStates[feature.properties.id]?.hasAlert)
          .map(feature => {
            const rippleProps = generateRippleProperties(feature.properties);
            return {
              type: 'Feature',
              geometry: {
                type: 'Point',
                coordinates: feature.geometry.coordinates
              },
              properties: {
                ...feature.properties,
                rippleColor: rippleProps.color,
                hasAlert: true
              }
            };
          });

        // Add ripple source
        const rippleSourceId = sourceId + '-ripple';
        sources[rippleSourceId] = {
          type: "geojson",
          data: {
            type: "FeatureCollection",
            features: rippleFeatures
          }
        };

        // Store layer info
        entityLayersByType[entityTypeId] = {
          sourceId,
          layerIds: [sourceId + '-ripple', sourceId + '-symbols', sourceId + '-circles', sourceId + '-labels'],
          entityTypeName: entityGroup.entityTypeName,
          entityTypeColor: entityGroup.entityTypeColor
        };
      }
    }
  });

  return { sources, entityLayersByType };
};

export const createEntityLayers = (entityLayersByType) => {
  const layers = [];

  // Create layers for each entity type
  Object.values(entityLayersByType).forEach((layerInfo) => {
    const sourceId = layerInfo.sourceId;
    const layerId = sourceId.replace('entities-', 'entities-');

    // Add ripple effect layer with pulsing animation
    layers.push({
      id: layerId + '-ripple',
      type: "circle",
      source: sourceId + '-ripple',
      layout: { visibility: "visible" },
      paint: {
        "circle-radius": 15, // Bigger base radius, will be animated
        "circle-color": ["get", "rippleColor"],
        "circle-opacity": 0.15,
        "circle-stroke-color": ["get", "rippleColor"],
        "circle-stroke-width": 3,
        "circle-stroke-opacity": 0.3
      }
    });

    // Add symbol layer for entities with icons
    layers.push({
      id: layerId + '-symbols',
      type: "symbol",
      source: sourceId,
      layout: {
        "icon-image": ["get", "icon"],
        "icon-size": [
          "interpolate",
          ["linear"],
          ["zoom"],
          10, 0.5,
          18, 1.5
        ],
        "icon-allow-overlap": true,
        "icon-ignore-placement": false,
        "visibility": "visible"
      },
      paint: {
        "icon-opacity": 0.9
      }
    });

    // Add circle layer as fallback for entities without icons
    layers.push({
      id: layerId + '-circles',
      type: "circle",
      source: sourceId,
      layout: { visibility: "visible" },
      paint: {
        "circle-radius": [
          "interpolate",
          ["linear"],
          ["zoom"],
          10, 4,
          18, 12
        ],
        "circle-color": ["get", "color"],
        "circle-stroke-color": "#ffffff",
        "circle-stroke-width": 2,
        "circle-opacity": 0.8
      }
    });

    // Add symbol layer for entity names
    layers.push({
      id: layerId + '-labels',
      type: "symbol",
      source: sourceId,
      layout: {
        "text-field": ["get", "name"],
        "text-font": ["Noto Sans Regular"],
        "text-size": 12,
        "text-anchor": "center",
        "text-offset": [0, -2],
        "text-allow-overlap": false,
        "text-ignore-placement": false,
        "visibility": "visible"
      },
      paint: {
        "text-color": "#333333",
        "text-halo-color": "#ffffff",
        "text-halo-width": 1,
        "text-halo-blur": 0.5,
      },
    });
  });

  return layers;
};

export const createEntityLayerVisibility = (entityLayersByType) => {
  const layerVisibility = {};

  // Add entity layer visibility
  Object.values(entityLayersByType).forEach((layerInfo) => {
    layerInfo.layerIds.forEach(layerId => {
      layerVisibility[layerId] = true;
    });
  });

  return layerVisibility;
};

// Helper functions for entity alert states and ripple effects
export const generateRippleProperties = (entity) => {
  const colors = [
    '#ff0000', '#ff4500', '#ff8c00', '#ffa500', '#ffd700', // Red to Yellow
    '#00ff00', '#32cd32', '#00ff7f', '#00fa9a', '#98fb98', // Green variations
    '#0000ff', '#4169e1', '#1e90ff', '#00bfff', '#87ceeb', // Blue variations
  ];
  
  const color = colors[Math.floor(Math.random() * colors.length)];
  
  return { color };
};

export const assignRandomAlertStates = (entities) => {
  const newAlertStates = {};
  let alertCount = 0;
  
  entities.forEach(entity => {
    // 30% chance of having an alert
    const hasAlert = Math.random() < 0.3;
    if (hasAlert) alertCount++;
    
    newAlertStates[entity._id] = {
      hasAlert,
      alertType: hasAlert ? ['sensor', 'incident', 'status'][Math.floor(Math.random() * 3)] : null,
      alertLevel: hasAlert ? Math.floor(Math.random() * 3) + 1 : 0, // 1-3 alert levels
      timestamp: Date.now()
    };
  });
  
  console.log(`Assigned alerts to ${alertCount} out of ${entities.length} entities`);
  return newAlertStates;
};

// New function to assign alerts to specific entity IDs only
export const assignStaticAlertStates = (entities, staticEntityIds = []) => {
  const newAlertStates = {};
  
  entities.forEach(entity => {
    // Only assign alert if entity ID is in the staticEntityIds array
    const hasAlert = staticEntityIds.includes(entity._id);
    
    newAlertStates[entity._id] = {
      hasAlert,
      alertType: hasAlert ? 'sensor' : null,
      alertLevel: hasAlert ? 2 : 0, // Medium alert level
      timestamp: Date.now()
    };
  });
  
  console.log(`Assigned alerts to ${staticEntityIds.length} specific entities out of ${entities.length} total entities`);
  console.log('Entity IDs with ripples:', staticEntityIds);
  return newAlertStates;
};

export const hasEntityAlert = (entity, entityAlertStates) => {
  return entityAlertStates[entity.id]?.hasAlert || false;
}; 